import {
  EIA_SERIES,
  FuelPriceType,
  PriceBuilderRow,
  RateRangeBuilderRow,
  SearchFuelSurchargeTableResponseDto
} from '../redux/api/fuelSurchargeTable/fuelSurchargeTable.model';
import {
  getInvoiceServiceUnitLabel,
  InvoiceServiceUnit
} from '../redux/api/invoiceService/invoiceService.model';
import { SearchRateTablesResponseDto } from '../redux/api/rateTable/rateTable.model';
import DateUtils from './dateUtils';
import NumberUtils from './numberUtils';

export enum CustomSourceError {
  PRICE_NOT_FOUND = 'PRICE_NOT_FOUND',
  RATE_NOT_FOUND = 'RATE_NOT_FOUND'
}

export type FuelSurchargeTableTooltip = {
  rate: number;
  price: number;
  unit: InvoiceServiceUnit;
  eiaAreaName: string;
  fuelPriceType: FuelPriceType;
  periodDate?: Date;
  loadPickupDate: string;
};

class FuelSurchargeTableUtils {
  static getCurrentRateOfEiaSource(
    price: number,
    rateRanges: SearchFuelSurchargeTableResponseDto['rate_ranges'],
    unit: InvoiceServiceUnit,
    withCurrency = true
  ): string | null | number {
    const rate = rateRanges.find((range) => price >= range.range_from && price <= range.range_to);

    if (!rate) {
      return null;
    }

    const label = getInvoiceServiceUnitLabel(unit);

    if (withCurrency) {
      return `${NumberUtils.getCurrency(rate.rate)} ${label.toLowerCase()}` || null;
    }

    return rate.rate;
  }

  static getCurrentRateOfCustomSource(
    prices: SearchFuelSurchargeTableResponseDto['prices'],
    rateRanges: SearchFuelSurchargeTableResponseDto['rate_ranges'],
    unit: InvoiceServiceUnit,
    withCurrency = true
  ): string | null | number {
    const todayUTC = this.getTodayUTC();
    const todayPrice = prices.find((price) => {
      const startDate = new Date(price.start_date);
      startDate.setUTCHours(0, 0, 0, 0);

      const endDate = new Date(price.end_date);
      endDate.setUTCHours(23, 59, 59, 999);

      return startDate <= todayUTC && endDate >= todayUTC;
    });

    if (!todayPrice) {
      return null;
    }

    const rate = rateRanges.find(
      (range) => todayPrice.price >= range.range_from && todayPrice.price <= range.range_to
    );
    if (!rate) {
      return null;
    }
    const label = getInvoiceServiceUnitLabel(unit);

    if (withCurrency) {
      return `${NumberUtils.getCurrency(rate.rate)} ${label.toLowerCase()}` || null;
    }

    return rate.rate;
  }

  static getCurrentRateOfCustomSourceByPickupDate(
    prices: SearchFuelSurchargeTableResponseDto['prices'],
    rateRanges: SearchFuelSurchargeTableResponseDto['rate_ranges'],
    unit: InvoiceServiceUnit,
    pickupDate: Date
  ): { rate: number; price: number; hasError: boolean; message?: CustomSourceError } {
    const todayUTC = this.getTodayUTC(pickupDate);
    const todayPrice = prices.find((price) => {
      const startDate = new Date(price.start_date);
      startDate.setUTCHours(0, 0, 0, 0);

      const endDate = new Date(price.end_date);
      endDate.setUTCHours(23, 59, 59, 999);

      return startDate <= todayUTC && endDate >= todayUTC;
    });

    if (!todayPrice) {
      return { rate: 0, price: 0, hasError: true, message: CustomSourceError.PRICE_NOT_FOUND };
    }

    const rate = rateRanges.find(
      (range) => todayPrice.price >= range.range_from && todayPrice.price <= range.range_to
    );

    if (!rate) {
      return { rate: 0, price: 0, hasError: true, message: CustomSourceError.RATE_NOT_FOUND };
    }

    return { rate: rate.rate, price: todayPrice.price, hasError: false };
  }
  static getCurrentPriceBasedOnToday(
    prices: SearchFuelSurchargeTableResponseDto['prices'],
    withCurrency = true
  ): string | null | number {
    const todayUTC = this.getTodayUTC();
    const todayPrice = prices.find((price) => {
      const startDate = new Date(price.start_date);
      startDate.setUTCHours(0, 0, 0, 0);

      const endDate = new Date(price.end_date);
      endDate.setUTCHours(23, 59, 59, 999);

      return startDate <= todayUTC && endDate >= todayUTC;
    });

    if (!todayPrice) {
      return null;
    }

    if (withCurrency) {
      return NumberUtils.getCurrency(todayPrice.price) || null;
    }

    return todayPrice.price || null;
  }

  static getNextStartDate(prices: PriceBuilderRow[]): { startDate: Date; endDate: Date } | null {
    const PLUS_DAY = 1;
    const DAY_IN_MS = 24 * 60 * 60 * 1000;
    const sortedPrices = [...prices].sort((a, b) => {
      if (a.start_date && b.start_date) {
        return new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
      }
      return 0;
    });

    const lastPrice = sortedPrices[sortedPrices.length - 1];

    if (lastPrice && lastPrice.start_date && lastPrice.end_date) {
      const rangeInDays = this.getRangeInDaysBetweenDates(
        new Date(lastPrice.start_date),
        new Date(lastPrice.end_date)
      );

      const lastEndDate = sortedPrices[sortedPrices.length - 1].end_date;

      if (!lastEndDate) {
        return null;
      }

      if (lastEndDate) {
        return {
          startDate: new Date(new Date(lastEndDate).getTime() + DAY_IN_MS),
          endDate: new Date(new Date(lastEndDate).getTime() + (rangeInDays + PLUS_DAY) * DAY_IN_MS)
        };
      }
    }

    return null;
  }

  private static getTodayUTC(loadPickupDate?: Date): Date {
    const nowLocal = loadPickupDate || new Date();
    const localYear = nowLocal.getFullYear();
    const localMonth = nowLocal.getMonth();
    const localDay = nowLocal.getDate();
    return new Date(Date.UTC(localYear, localMonth, localDay));
  }

  static getFuelPriceSourceLabel(areaId: string): string {
    const source = EIA_SERIES.find((series) => series.id === areaId);
    return source?.label || '-';
  }

  private static getRangeInDaysBetweenDates(startDate: Date, endDate: Date): number {
    const timeDiff = Math.abs(new Date(endDate).getTime() - new Date(startDate).getTime());
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  }

  private static getRateRange(rangeFrom: number, rangeTo: number): number {
    const range = rangeTo - rangeFrom;
    return range;
  }

  static getNextRateRange(rateRanges: RateRangeBuilderRow[]):
    | {
        range_from: number;
        range_to: number;
      }
    | undefined {
    const sortedRateRanges = [...rateRanges].sort((a, b) => {
      if (a.range_from && b.range_from) {
        return a.range_from - b.range_from;
      }
      return 0;
    });

    const lastRateRange = sortedRateRanges[sortedRateRanges.length - 1];

    if (lastRateRange && lastRateRange.range_from && lastRateRange.range_to) {
      const range = this.getRateRange(+lastRateRange.range_from, +lastRateRange.range_to);
      const nextRageFrom = this.getNextRangeFrom(lastRateRange.range_to.toString());
      const nextRangeTo = Number((nextRageFrom + range).toFixed(4));
      return {
        range_from: nextRageFrom,
        range_to: nextRangeTo
      };
    }

    return undefined;
  }

  static getNextRangeFrom(previousRangeTo: string): number {
    const decimalPlaces = (previousRangeTo.split('.')[1] || '').length;
    const increment = Math.pow(0.1, decimalPlaces);
    return Number((+previousRangeTo + increment).toFixed(decimalPlaces));
  }

  static determineFuelPrice(
    fuelSurchargeTable: SearchRateTablesResponseDto['fuel_surcharge_table'],
    pickupDate: Date,
    newPrice?: number | null
  ): {
    rate: number;
    price: number;
    hasError: boolean;
    message?: string;
  } {
    if (!fuelSurchargeTable) {
      return { rate: 0, price: 0, hasError: true, message: CustomSourceError.PRICE_NOT_FOUND };
    }

    const fuelPriceType = fuelSurchargeTable.fuel_price_type;
    const fuelPrice = newPrice || fuelSurchargeTable.price;

    if (fuelPriceType === 'EIA') {
      if (newPrice === null) {
        return {
          rate: 0,
          price: 0,
          hasError: true,
          message: 'No diesel price data found for the selected date from EIA'
        };
      }

      if (!fuelSurchargeTable.rate_ranges) {
        return { rate: 0, price: 0, hasError: true, message: CustomSourceError.RATE_NOT_FOUND };
      }
      if (!fuelPrice) {
        return { rate: 0, price: 0, hasError: true, message: CustomSourceError.PRICE_NOT_FOUND };
      }

      const currentRate = this.getCurrentRateOfEiaSource(
        fuelPrice,
        fuelSurchargeTable.rate_ranges,
        fuelSurchargeTable.unit,
        false
      );

      if (!currentRate) {
        return {
          rate: 0,
          price: 0,
          hasError: true,
          message:
            'Fuel price not found. Make sure the fuel price is covered in the "Rates" section of the fuel surcharge table'
        };
      }

      return { rate: Number(currentRate), price: fuelPrice, hasError: false };
    } else {
      if (!fuelSurchargeTable.prices) {
        throw new Error('Fuel surcharge table prices not found');
      }
      if (!fuelSurchargeTable.rate_ranges) {
        throw new Error('Fuel surcharge table rate ranges not found');
      }

      const { rate, price, hasError, message } = this.getCurrentRateOfCustomSourceByPickupDate(
        fuelSurchargeTable.prices,
        fuelSurchargeTable.rate_ranges,
        fuelSurchargeTable.unit,
        pickupDate
      );

      if (hasError) {
        const errorMessage =
          message === CustomSourceError.PRICE_NOT_FOUND
            ? 'Missing fuel price required. Make sure the load pickup date is covered in a date range within the fuel surcharge table'
            : 'Fuel price not found. Make sure the fuel price is covered in the "Rates" section of the fuel surcharge table';
        return { rate: 0, price: 0, hasError: true, message: errorMessage };
      }

      return { rate: Number(rate), price: price, hasError: false };
    }
  }

  static generateFuelSurchargeTableTooltip({
    rate,
    price,
    unit,
    fuelPriceType,
    eiaAreaName,
    periodDate,
    loadPickupDate
  }: FuelSurchargeTableTooltip): string {
    const isoPeriodDate = periodDate ? new Date(periodDate).toISOString() : '';
    const unitLabel = getInvoiceServiceUnitLabel(unit).toLowerCase();
    if (fuelPriceType === 'EIA') {
      return `Rate of $${rate} ${unitLabel} is based on the ${eiaAreaName} ${DateUtils.formatPeriodDate(
        isoPeriodDate
      )} fuel price of $${price}`;
    }

    return `Rate of $${rate} ${unitLabel} is based on the ${loadPickupDate} fuel price of $${price}`;
  }

  /**
   * This function is used to get the max value for decimal places
   * @param value - the value to get the max value for
   * @returns the max value for decimal places
   * e.g 3.X -> 3.X9999
   * e.g 3.XX -> 3.XX99
   * e.g 3.XXX -> 3.XXX9
   * e.g 3.XXXX -> 3.XXXX
   * this is in order to cover max range of 4 decimal places
   */
  static getMaxValueForDecimalPlaces(value: string): string {
    if (!value) return '';
    if (!value.includes('.')) return value;

    const [integer, decimal] = value.split('.');
    const decimalLength = decimal.length;
    const remainingPlaces = 4 - decimalLength;

    if (remainingPlaces <= 0) return value;
    return `${integer}.${decimal}${'9'.repeat(remainingPlaces)}`;
  }
}

export default FuelSurchargeTableUtils;
